import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Re-run `load` on an interval, but only while the tab is visible.
 *
 * A socket would not make this data any fresher: the agent reports on its
 * heartbeat, so the ceiling is the agent, not the transport. Polling at the same
 * interval gives identical freshness with no server-side fan-out — the admin
 * report is company-wide and has no per-record recipient to emit to. Discrete
 * events with a known recipient (a check-in/check-out punch) use the socket
 * instead.
 *
 * Pausing on hidden is not an optimisation: admins leave this page open for
 * hours, and a background tab polling forever is pure waste. Becoming visible
 * refreshes immediately, because whatever is on screen is by then stale.
 *
 * Returns the instant of the last completed load, for an "updated Xs ago" stamp.
 * A stale number that looks live is the specific way the "Right now" strip
 * misleads, so callers are expected to render it.
 */
export default function useAutoRefresh(load, { intervalMs = 60000, enabled = true } = {}) {
    const [lastUpdated, setLastUpdated] = useState(null);

    // Held in a ref so changing `load` does not tear down and restart the timer.
    const loadRef = useRef(load);
    useEffect(() => { loadRef.current = load; }, [load]);

    const run = useCallback(() => {
        // silent: a background poll must not put the screen into its loading
        // state. What is already rendered stays valid until the new data lands.
        const result = loadRef.current(true);
        // Stamp when the load actually finished, so the age is honest about
        // slow responses. A non-promise loader stamps immediately.
        if (result && typeof result.finally === 'function') {
            result.finally(() => setLastUpdated(new Date()));
        } else {
            setLastUpdated(new Date());
        }
    }, []);

    // Mark the first load, which the caller's own effect performs.
    useEffect(() => { setLastUpdated(new Date()); }, []);

    useEffect(() => {
        if (!enabled) return undefined;

        let timer = null;
        const start = () => {
            if (timer === null) timer = setInterval(run, intervalMs);
        };
        const stop = () => {
            if (timer !== null) { clearInterval(timer); timer = null; }
        };

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                stop();
            } else {
                run();
                start();
            }
        };

        if (document.visibilityState !== 'hidden') start();
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [enabled, intervalMs, run]);

    return lastUpdated;
}

/** A Date -> 'updated just now' / 'updated 42s ago' / 'updated 3m ago'. */
export function updatedAgo(at, now = Date.now()) {
    if (!at) return '';
    const seconds = Math.max(0, Math.round((now - at.getTime()) / 1000));
    if (seconds < 10) return 'updated just now';
    if (seconds < 60) return `updated ${seconds}s ago`;
    if (seconds < 3600) return `updated ${Math.floor(seconds / 60)}m ago`;
    return `updated ${Math.floor(seconds / 3600)}h ago`;
}

/**
 * The age stamp, ticking on its own.
 *
 * It re-renders every 10s rather than reading the clock once, because the whole
 * point is to show a number going stale — a label frozen at "updated just now"
 * for ten minutes is worse than showing nothing.
 */
export function UpdatedAgo({ at }) {
    const [, setTick] = useState(0);

    useEffect(() => {
        if (!at) return undefined;
        const timer = setInterval(() => setTick((n) => n + 1), 10000);
        return () => clearInterval(timer);
    }, [at]);

    if (!at) return null;
    return <span className="wt-updated">{updatedAgo(at)}</span>;
}
